import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { ToastContext } from '../context/ToastContext'; // <-- Import kontekstu
import axios from 'axios';
import '../styles/ProductCard.css';
import ButtonRegular from './ui/ButtonRegular';
import ButtonAdditional from './ui/ButtonAdditional';

function ProductCard({ product }) {
    const navigate = useNavigate();
    const { isLoggedIn } = useContext(AuthContext);
    const { fetchCart } = useContext(CartContext);
    const { showToast } = useContext(ToastContext); // <-- Pobranie funkcji wywołującej Toast

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);

    const productId = product?._id;

    useEffect(() => {
        const checkFavorite = async () => {
            if (!isLoggedIn || !productId) return;

            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/favorites`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                });

                const favorites = res.data.items || [];
                setIsFavorite(favorites.some((fav) => (fav.product?._id || fav.product) === productId));
            } catch (error) {
                console.error('Błąd pobierania ulubionych:', error);
            }
        };

        checkFavorite();
    }, [isLoggedIn, productId]);

    if (!product) return null;

    const availableStock = Math.max(0, (product.stock || 0) - (product.reserved || 0));
    const isOutOfStock = availableStock <= 0;

    const images = Array.isArray(product.images) && product.images.length > 0
        ? product.images
        : ['https://via.placeholder.com/300x200?text=Brak+zdjecia'];

    const getImgSrc = (img) => {
        if (!img) return 'https://via.placeholder.com/300x200?text=Brak+zdjecia';
        const rawPath = typeof img === 'object' ? (img.url || img.path) : img;
        if (!rawPath) return 'https://via.placeholder.com/300x200?text=Brak+zdjecia';
        if (rawPath.startsWith('http') || rawPath.startsWith('data:')) return rawPath;
        const cleanPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
        return `${process.env.REACT_APP_API_URL}${cleanPath}`;
    };

    const handleToggleFavorite = async (e) => {
        e.stopPropagation();

        if (!isLoggedIn) {
            showToast('Musisz być zalogowany, aby dodać produkt do ulubionych.');
            navigate('/login');
            return;
        }

        try {
            if (isFavorite) {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/api/favorites/remove/${product._id}`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(false);
                showToast('💔 Usunięto z ulubionych');
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/favorites/add`,
                    { productId: product._id },
                    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
                );
                setIsFavorite(true);
                showToast('❤️ Dodano do ulubionych!');
            }
        } catch (error) {
            console.error('Błąd ulubionych:', error);
            showToast('❌ Błąd przy modyfikacji ulubionych');
        }
    };

    const handleAddToCart = async (e) => {
        e.stopPropagation();

        if (isOutOfStock) return;

        if (!isLoggedIn) {
            showToast('Musisz być zalogowany, aby dodać produkt do koszyka.');
            navigate('/login');
            return;
        }

        try {
            setLoading(true);

            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/cart/add`,
                {
                    productId: product._id,
                    quantity: 1,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                    },
                }
            );

            await fetchCart();
            showToast('✅ Produkt dodany do koszyka!');
        } catch (error) {
            console.error('Błąd dodawania do koszyka:', error);
            showToast('❌ Wystąpił błąd przy dodawaniu do koszyka.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const handleDotClick = (index, e) => {
        e.stopPropagation();
        setCurrentImageIndex(index);
    };

    return (
        <div
            className={`product-card ${isOutOfStock ? 'out-of-stock' : ''}`}
            onClick={() => navigate(`/products/${product._id}`)}
        >
            <div className="carousel-wrapper">
                {isOutOfStock && <span className="out-of-stock-badge">Sprzedane</span>}

                <button
                    className={`fav-icon-button ${isFavorite ? 'active' : ''}`}
                    onClick={handleToggleFavorite}
                    title={isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'}
                    type="button"
                >
                    {isFavorite ? '❤️' : '🤍'}
                </button>

                <div
                    className="carousel"
                    style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                >
                    {images.map((img, idx) => (
                        <img
                            key={idx}
                            src={getImgSrc(img)}
                            alt={product.name ? `${product.name} - zdjęcie ${idx + 1}` : `product-${idx}`}
                            className={`carousel-image ${isOutOfStock ? 'grayscale-img' : ''}`}
                        />
                    ))}
                </div>

                {images.length > 1 && (
                    <>
                        <button className="arrow left" onClick={handlePrev} aria-label="Poprzednie zdjęcie">
                            &#10094;
                        </button>
                        <button className="arrow right" onClick={handleNext} aria-label="Następne zdjęcie">
                            &#10095;
                        </button>

                        <div className="dots">
                            {images.map((_, index) => (
                                <span
                                    key={index}
                                    className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                                    onClick={(e) => handleDotClick(index, e)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="product-info">
                <h3 className="product-name" title={product.name}>{product.name}</h3>

                <div className="product-price-section">
                    <div className="product-price">
                        {Number(product.price).toFixed(2)} <span className="currency">PLN</span>
                    </div>
                </div>

                <div className="product-buttons">
                    <ButtonRegular
                        onClick={handleAddToCart}
                        disabled={loading || isOutOfStock}
                        type="button"
                    >
                        {isOutOfStock ? 'Niedostępny' : loading ? 'Dodaję...' : 'Dodaj do koszyka'}
                    </ButtonRegular>

                    <ButtonAdditional
                        onClick={handleToggleFavorite}
                        type="button"
                    >
                        {isFavorite ? '❤️ Usuń z ulubionych' : '🤍 Dodaj do ulubionych'}
                    </ButtonAdditional>
                </div>
            </div>
        </div>
    );
}

export default ProductCard;